import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import type { DepartmentReportDocument, UpsertDepartmentReportDocumentPayload } from "@features/department/api/departmentReportDocumentApi";
import type { OriginScope } from "@features/report/template/types/reportTemplate";

function todayIsoDate(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const ORIGIN_SCOPE_OPTIONS: { value: OriginScope; label: string }[] = [
  { value: "CHURCH", label: "Church" },
  { value: "DISTRICT", label: "District" },
  { value: "NATION", label: "Nation" },
];

type Props = {
  show: boolean;
  slot: DepartmentReportDocument | null;
  nextListOrder: number;
  busy: boolean;
  availableOriginScopes: OriginScope[];
  onHide: () => void;
  onSave: (payload: UpsertDepartmentReportDocumentPayload) => void;
};

export default function DepartmentReportDocumentModal({
  show,
  slot,
  nextListOrder,
  busy,
  availableOriginScopes,
  onHide,
  onSave,
}: Props) {
  const isEdit = slot != null;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isRequired, setIsRequired] = useState(true);
  const [requiredAt, setRequiredAt] = useState(todayIsoDate());
  const [listOrder, setListOrder] = useState(0);
  const [originScope, setOriginScope] = useState<OriginScope | "">("");

  const originOptions = useMemo(
    () => ORIGIN_SCOPE_OPTIONS.filter((opt) => availableOriginScopes.includes(opt.value)),
    [availableOriginScopes]
  );

  useEffect(() => {
    if (!show) return;
    if (slot) {
      setName(slot.name);
      setDescription(slot.description ?? "");
      setIsRequired(slot.is_required);
      setRequiredAt(slot.required_at ?? todayIsoDate());
      setListOrder(slot.list_order);
      setOriginScope(slot.origin_scope);
    } else {
      setName("");
      setDescription("");
      setIsRequired(true);
      setRequiredAt(todayIsoDate());
      setListOrder(nextListOrder);
      setOriginScope(originOptions.length === 1 ? originOptions[0].value : "");
    }
  }, [show, slot, nextListOrder, originOptions]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    if (!isEdit && !originScope) return;
    onSave({
      name: trimmed,
      description: description.trim() || null,
      is_required: isRequired,
      required_at: isRequired ? requiredAt : null,
      list_order: Number.isFinite(listOrder) ? listOrder : 0,
      ...(isEdit ? {} : { origin_scope: originScope as OriginScope }),
    });
  };

  return (
    <Modal show={show} onHide={busy ? undefined : onHide} centered backdrop="static" keyboard={!busy}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!busy}>
          <Modal.Title>{isEdit ? "Edit document category" : "Add document category"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>To be completed by</Form.Label>
            {isEdit ? (
              <Form.Control
                value={ORIGIN_SCOPE_OPTIONS.find((o) => o.value === originScope)?.label || originScope}
                disabled
                readOnly
              />
            ) : originOptions.length === 0 ? (
              <Form.Text className="text-danger d-block">No origin scopes available for your permissions.</Form.Text>
            ) : originOptions.length === 1 ? (
              <Form.Control value={originOptions[0].label} disabled readOnly />
            ) : (
              <Form.Select
                value={originScope}
                onChange={(e) => setOriginScope(e.target.value as OriginScope | "")}
                required
                disabled={busy}
                data-testid="document-origin-scope-select"
              >
                <option value="">Select who uploads this…</option>
                {originOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              required
              disabled={busy}
              data-testid="document-name-input"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Help text</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              disabled={busy}
            />
          </Form.Group>
          <Form.Check
            type="switch"
            id="document-required"
            className="mb-3"
            label="Required"
            checked={isRequired}
            onChange={(e) => {
              const next = e.target.checked;
              setIsRequired(next);
              if (next && !requiredAt) setRequiredAt(todayIsoDate());
            }}
            disabled={busy}
          />
          {isRequired ? (
            <Form.Group className="mb-3">
              <Form.Label>Required starting</Form.Label>
              <Form.Control
                type="date"
                value={requiredAt}
                onChange={(e) => setRequiredAt(e.target.value)}
                required
                disabled={busy}
              />
            </Form.Group>
          ) : null}
          <Form.Group>
            <Form.Label>List order</Form.Label>
            <Form.Control
              type="number"
              min={0}
              value={listOrder}
              onChange={(e) => setListOrder(Number(e.target.value))}
              disabled={busy}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={busy || !name.trim() || (!isEdit && !originScope)}
          >
            {busy ? <Spinner animation="border" size="sm" aria-hidden /> : isEdit ? "Save" : "Add"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
